import * as React from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { DataTableBlock, ListPage, type ListPageTableState } from '@/components/templates/list-page'
import { Alert } from '@/components/ui/alert'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { getJSON, postJSON } from '@/lib/api'
import { useViewOnly } from '@/components/templates/app-shell'
import { useEmailLogSelection } from './useEmailLogSelection'
import { EmailLogDetailSheet } from './EmailLogDetailSheet'
import { OwnerFilter } from '@/components/OwnerFilter'
import { emailSendColumns, statusLabels } from './EmailLogTable'
import { useEmailSendPages } from './EmailLogPagination'

interface ContactsResponse {
  contacts: { id: number; name: string }[]
}

interface EmailLogFilters {
  contactId: string
  kind: string
  status: string
  sentFrom: string
  sentThrough: string
  owner?: string
}

const emptyFilters: EmailLogFilters = {
  contactId: 'all',
  kind: 'all',
  status: 'all',
  sentFrom: '',
  sentThrough: '',
  owner: undefined,
}

export function EmailLogPage() {
  const selection = useEmailLogSelection('email-log')
  const viewOnly = useViewOnly()
  const queryClient = useQueryClient()
  const [filters, setFilters] = React.useState(emptyFilters)
  const { query, pagination } = useEmailSendPages(emailSendsPath(filters))
  const contactsQuery = useQuery({
    queryKey: ['contacts', 'email-log-filter'],
    queryFn: () => getJSON<ContactsResponse>('/api/v1/contacts?include_archived=true'),
    retry: false,
  })
  const resend = useMutation({
    mutationFn: (id: number) => postJSON(`/api/v1/email_sends/${id}/resend`, {}),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['email-sends'] }),
  })

  return (
    <>
      <ListPage
        title="Email log"
        toolbar={
          <>
            <EmailLogToolbar
              filters={filters}
              contacts={contactsQuery.data?.contacts ?? []}
              onChange={setFilters}
            />
            {resend.error ? <Alert>{resend.error.message}</Alert> : null}
          </>
        }
        table={{
          rows: query.data?.email_sends ?? [],
          getRowId: (send) => send.id,
          onRowOpen: (send) => selection.open(send.id),
          getRowLabel: (send) =>
            `${send.kind_label} to ${send.contact_name ?? send.recipient_email ?? 'recipient'}`,
          state: query.isLoading ? 'loading' : query.isError ? 'error' : 'ready',
          loadingTitle: 'Loading Email log',
          errorTitle: 'Email log unavailable',
          emptyTitle: 'No product emails sent yet',
          actions: () =>
            viewOnly
              ? []
              : [
                  {
                    label: 'Resend',
                    onSelect: (send) => resend.mutate(send.id),
                    disabled: resend.isPending,
                  },
                ],
          columns: emailSendColumns(true),
          footer: pagination,
        }}
      />
      <EmailLogDetailSheet id={selection.id} scope={selection.scope} onClose={selection.close} />
    </>
  )
}

export function ContactEmailHistory({ contactId }: { contactId: number }) {
  const selection = useEmailLogSelection(`contact:${contactId}`)
  const { query, pagination } = useEmailSendPages(`/api/v1/email_sends?contact_id=${contactId}`)

  return (
    <section className="space-y-3">
      <h3 className="text-body font-semibold text-foreground">Email history</h3>
      <div className="overflow-hidden rounded-md border border-border bg-card">
        <DataTableBlock
          table={{
            rows: query.data?.email_sends ?? [],
            columns: emailSendColumns(false),
            getRowId: (send) => send.id,
            onRowOpen: (send) => selection.open(send.id),
            getRowLabel: (send) => send.kind_label,
            state: tableState(query.isLoading, query.isError),
            loadingTitle: 'Loading email history',
            errorTitle: 'Email history unavailable',
            emptyTitle: 'No emails recorded',
            footer: pagination,
          }}
        />
      </div>
      <EmailLogDetailSheet id={selection.id} scope={selection.scope} onClose={selection.close} />
    </section>
  )
}

function EmailLogToolbar({
  filters,
  contacts,
  onChange,
}: {
  filters: EmailLogFilters
  contacts: ContactsResponse['contacts']
  onChange: React.Dispatch<React.SetStateAction<EmailLogFilters>>
}) {
  return (
    <>
      <FilterSelect
        label="Contact"
        value={filters.contactId}
        options={contacts.map((contact) => ({ value: String(contact.id), label: contact.name }))}
        allLabel="All contacts"
        onChange={(contactId) => onChange((current) => ({ ...current, contactId }))}
      />
      <OwnerFilter
        value={filters.owner}
        onChange={(owner) => onChange((current) => ({ ...current, owner }))}
      />
      <FilterSelect
        label="Email kind"
        value={filters.kind}
        options={Object.entries(kindLabels).map(([value, label]) => ({ value, label }))}
        allLabel="All kinds"
        onChange={(kind) => onChange((current) => ({ ...current, kind }))}
      />
      <FilterSelect
        label="Delivery status"
        value={filters.status}
        options={Object.entries(statusLabels).map(([value, label]) => ({ value, label }))}
        allLabel="All statuses"
        onChange={(status) => onChange((current) => ({ ...current, status }))}
      />
      <label
        htmlFor="email-log-sent-from"
        className="flex items-center gap-2 text-caption text-muted-foreground"
      >
        <span>From</span>
        <Input
          id="email-log-sent-from"
          aria-label="Sent from"
          type="date"
          className="w-40"
          value={filters.sentFrom}
          onChange={(event) =>
            onChange((current) => ({ ...current, sentFrom: event.target.value }))
          }
        />
      </label>
      <label
        htmlFor="email-log-sent-through"
        className="flex items-center gap-2 text-caption text-muted-foreground"
      >
        <span>Through</span>
        <Input
          id="email-log-sent-through"
          aria-label="Sent through"
          type="date"
          className="w-40"
          value={filters.sentThrough}
          onChange={(event) =>
            onChange((current) => ({ ...current, sentThrough: event.target.value }))
          }
        />
      </label>
    </>
  )
}

function FilterSelect({
  label,
  value,
  options,
  allLabel,
  onChange,
}: {
  label: string
  value: string
  options: { value: string; label: string }[]
  allLabel: string
  onChange: (value: string) => void
}) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger aria-label={label} className="w-44">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">{allLabel}</SelectItem>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

const kindLabels = {
  blast_reputation_alert: 'Blast reputation alert',
  sending_domain_degraded_alert: 'Sending Domain degraded alert',
  renewal_invoice_ready: 'Renewal invoice ready',
  autopay_enrollment_confirmation: 'Autopay enrollment confirmation',
  upcoming_charge: 'Upcoming charge',
  card_expiring: 'Card expiring',
  autopay_charge_failed: 'Autopay charge failed',
  autopay_disabled: 'Autopay disabled',
  payment_receipt: 'Payment receipt',
  application_approved: 'Application approved',
  application_rejected: 'Application rejected',
  application_received: 'Application received',
  event_order_confirmation: 'Event Order confirmation',
  event_invoice: 'Event Invoice',
  accounting_connection_alert: 'Accounting connection alert',
  morning_digest: 'Morning digest',
}

function emailSendsPath(filters: EmailLogFilters) {
  const params = new URLSearchParams()
  if (filters.contactId !== 'all') params.set('contact_id', filters.contactId)
  if (filters.kind !== 'all') params.set('kind', filters.kind)
  if (filters.status !== 'all') params.set('status', filters.status)
  if (filters.sentFrom) params.set('sent_from', filters.sentFrom)
  if (filters.sentThrough) params.set('sent_through', filters.sentThrough)
  if (filters.owner) params.set('owner', filters.owner)
  const query = params.toString()
  return query ? `/api/v1/email_sends?${query}` : '/api/v1/email_sends'
}

function tableState(isLoading: boolean, isError: boolean): ListPageTableState {
  if (isLoading) return 'loading'
  if (isError) return 'error'
  return 'ready'
}
